import { useEffect } from 'react';
import { useAuth } from '@/contexts/useAuth';
import {
  MESSENGER_OPEN_AGENT_CHAT_FROM_NOTIFICATION,
  MESSENGER_OPEN_THREAD_FROM_NOTIFICATION,
  type MessengerOpenAgentChatFromNotificationDetail,
  type MessengerOpenThreadFromNotificationDetail,
} from '@/constants/messengerEvents';
import { installMessengerNotificationChannel } from '@/notifications/installMessengerNotificationChannel';
import { syncWebPushSubscriptionWithServer } from '@/notifications/webPushSubscribe';
import { installMessengerReconnectReconcile } from '@/components/Messenger/cache/messengerReconnectReconcile';

export function NotificationSync() {
  const { user, isAuthenticated } = useAuth();
  const personId = isAuthenticated ? user?.id ?? null : null;

  useEffect(() => installMessengerNotificationChannel(personId), [personId]);

  useEffect(() => {
    if (!personId) return;
    return installMessengerReconnectReconcile();
  }, [personId]);

  useEffect(() => {
    if (!personId) return;
    const run = () => void syncWebPushSubscriptionWithServer();
    run();
    const onVis = () => {
      if (document.visibilityState === 'visible') run();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [personId]);

  useEffect(() => {
    const onSw = (event: MessageEvent) => {
      const d = event.data as
        | {
            type?: string;
            detail?:
              | ({ kind?: 'thread' } & MessengerOpenThreadFromNotificationDetail)
              | ({ kind?: 'agent'; agentChatId?: string } & Partial<MessengerOpenAgentChatFromNotificationDetail>)
              | MessengerOpenThreadFromNotificationDetail
              | undefined;
          }
        | undefined;
      if (d?.type !== 'MESSENGER_PUSH_NAVIGATE' || !d.detail) return;

      const detail = d.detail as Record<string, unknown>;
      if (detail.kind === 'agent' && typeof detail.agentChatId === 'string') {
        window.dispatchEvent(
          new CustomEvent(MESSENGER_OPEN_AGENT_CHAT_FROM_NOTIFICATION, {
            detail: { chatId: detail.agentChatId },
          })
        );
        return;
      }
      if (typeof detail.agentChatId === 'string' && detail.agentChatId.trim()) {
        window.dispatchEvent(
          new CustomEvent(MESSENGER_OPEN_AGENT_CHAT_FROM_NOTIFICATION, {
            detail: { chatId: detail.agentChatId.trim() },
          })
        );
        return;
      }
      if (typeof detail.threadId === 'string' && detail.threadId.trim()) {
        const threadDetail: MessengerOpenThreadFromNotificationDetail = {
          threadId: detail.threadId,
          threadKind: detail.threadKind === 'group' ? 'group' : 'direct',
          peerId: typeof detail.peerId === 'string' ? detail.peerId : null,
          peerName: typeof detail.peerName === 'string' ? detail.peerName : null,
          title: typeof detail.title === 'string' ? detail.title : null,
        };
        window.dispatchEvent(
          new CustomEvent(MESSENGER_OPEN_THREAD_FROM_NOTIFICATION, { detail: threadDetail })
        );
      }
    };
    navigator.serviceWorker?.addEventListener('message', onSw);
    return () => navigator.serviceWorker?.removeEventListener('message', onSw);
  }, []);

  return null;
}

